import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";

/**
 * White surface card used by every dashboard widget. Radius/border/shadow map to
 * the handoff card token (§Design Tokens). Kept token-only per the repo rule.
 */
export function DashboardCard({
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(
				"rounded-2xl border border-base-300 bg-base-100 shadow-sm",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	);
}

/**
 * Independent error state for a single widget. A failing endpoint degrades only
 * its own card (graceful-degradation rule §6); the retry re-runs that query.
 */
export function WidgetError({ onRetry }: { onRetry?: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
			<AlertTriangle className="h-6 w-6 text-error" aria-hidden="true" />
			<p className="text-sm text-base-content/70">
				{t("dashboard.home.widgetError")}
			</p>
			{onRetry && (
				<button
					type="button"
					onClick={onRetry}
					className="rounded-lg border border-base-300 px-3 py-1.5 text-sm font-medium text-base-content transition-colors hover:bg-base-200"
				>
					{t("common.retry")}
				</button>
			)}
		</div>
	);
}

/** Empty state for a widget that loaded successfully but has no rows. */
export function WidgetEmpty({
	icon,
	message,
}: {
	icon?: ReactNode;
	message: string;
}) {
	return (
		<div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-base-content/50">
			{icon}
			<p className="text-sm">{message}</p>
		</div>
	);
}
